// Copyright (c) Docugami, Inc. All rights reserved.

import { AppState } from '../../../store';
import { AAEnableContextualSuggestionModeSchema, AAEnableGlobalSuggestionModeSchema, SettingsPanelOpenedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { FeatureFlags } from 'store/featureFlags/types';
import { FeatureFlagToggle } from 'components/feature_flags/FeatureFlagToggle';
import { Panel, Toggle, PrimaryButton, Separator, Dropdown } from "@fluentui/react";
import { getNumberOfChunkSuggestionsForDemoMode, setLocalStorageAssistedAuthoringContextModeValue, setNumberOfChunkSuggestionsForDemoMode } from '../../../utils/localStorageHelpers';
import { setSettingsPanelShowing, setWordTaskpaneShowing, setAssistedAuthoringSettings } from '../../../store/settings/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import 'components/settings/css/SettingsPanel.scss'
import { useFeatureFlag } from 'hooks/FeatureFlags';
import { TelemetryContextPanel } from 'telemetry/TelemetryContextPanel';

export const internalSettingsEnabled: boolean = process.env.REACT_APP_ENV === "staging" || process.env.REACT_APP_ENV === "dev";

export const SettingsPanel = (): JSX.Element => {
    const show = useSelector((state: AppState) => state.settings.settingsPanelShowing);
    const dispatch = useDispatch();

    useEffect(() => {
        if (show) {
            Telemetry.trackEvent<SettingsPanelOpenedSchema>({
                eventName: TelemetryEvent.SettingsPanelOpened
            });
        }
    }, [show]);

    function closePanel(): void {
        dispatch(setSettingsPanelShowing(false));
    }

    return (
        <Panel
            isOpen={show}
            isLightDismiss={true}
            isFooterAtBottom={true}
            onDismiss={closePanel}
            onLightDismissClick={closePanel}
            styles={{ main: { top: "50px" } }}
            headerText="Settings">
            {!!(window as any)._OfficeMode ? <WordAddInSettings /> : <WebappSettings />}
        </Panel>
    )
}

const WordAddInSettings = (): JSX.Element | null => {
    const dispatch = useDispatch();
    const aaSettings = useSelector((state: AppState) => state.settings.assistedAuthoring);
    const showWordTestTaskpane = useSelector((state: AppState) => state.settings.assistedAuthoring.showWordTestTaskpane)

    // If we are not in office, return null
    if (!(window as any)._OfficeMode) {
        return null;
    }

    function setAssistedAuthoringContextMode(contextModeOn: boolean) {
        dispatch(setAssistedAuthoringSettings(
            {
                ...aaSettings,
                contextMode: contextModeOn
            }));

        setLocalStorageAssistedAuthoringContextModeValue(contextModeOn);

        if (contextModeOn) {
            Telemetry.trackEvent<AAEnableContextualSuggestionModeSchema>({
                eventName: TelemetryEvent.AAEnableContextualSuggestionMode
            });
        } else {
            Telemetry.trackEvent<AAEnableGlobalSuggestionModeSchema>({
                eventName: TelemetryEvent.AAEnableGlobalSuggestionMode
            });
        }
    }

    const settings = [
        <Toggle
            key="ContextMode"
            checked={aaSettings.contextMode}
            label="Contextual Suggestion Mode"
            onText="On"
            offText="Off"
            onChange={() => setAssistedAuthoringContextMode(!aaSettings.contextMode)} />
    ];

    if (internalSettingsEnabled) {
        settings.push(
            <Toggle
                key="ShowTestTaskpaneToggle"
                checked={showWordTestTaskpane}
                label="Show Test Taskpane"
                onText="On"
                offText="Off"
                onChange={() => dispatch(setWordTaskpaneShowing(!showWordTestTaskpane))} />
        )
    }

    return <div style={{ display: "flex", flexDirection: "column" }}>{settings}</div>;
}

const WebappSettings = (): JSX.Element => {
    const diagnosticLoggingEnabled = useFeatureFlag(FeatureFlags.DiagnosticLoggingEnabled);

    return (
        <>
            <DebugSettings />
            <FeatureFlagSettings />
            {diagnosticLoggingEnabled && <TelemetryContextPanel />}
        </>
    );
}

const DebugSettings = (): JSX.Element => {
    function testCrashTelemetry(): void {
        throw new Error('Test Crash: Nothing to worry about');
    }

    return (
        <div className="settingsCollection">
            <Separator className="settingsSeparator">Debug</Separator>
            <PrimaryButton
                key="TestCrashTelemetryButton"
                onClick={testCrashTelemetry}>Test Crash Telemetry</PrimaryButton>
        </div>
    )
}

const NumChunkSuggestionsDropDown = (): JSX.Element => {
    return <Dropdown
        label={"Max Chunk Suggestions"}
        multiSelect={false}
        options={[10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 1000].map(x => { return { text: x.toString(), key: x } })}
        defaultSelectedKey={getNumberOfChunkSuggestionsForDemoMode()}
        onChange={(ev, option) => { if (option?.key) setNumberOfChunkSuggestionsForDemoMode(option.key as number) }} />
}

const FeatureFlagSettings = (): JSX.Element => {
    const demoModeEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);
    const chatEnabled = useFeatureFlag(FeatureFlags.ChatFeature);

    return (
        <div className="settingsCollection">
            <Separator className="settingsSeparator">Feature Flags</Separator>
            <FeatureFlagToggle featureFlag={FeatureFlags.DiagnosticLoggingEnabled} label="Diagnostic Logging" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DownloadAllPipelineOutput} label="Download all Pipeline Output" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DemoFeatures} label="Demo Features" />
            {demoModeEnabled ? <NumChunkSuggestionsDropDown /> : null}
            <FeatureFlagToggle featureFlag={FeatureFlags.ChunkSourceDebugEnabled} label="Chunk Source Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.DebugTokensEnabled} label="Tokens Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.SdvDebugEnabled} label="SDV Debug" />
            <FeatureFlagToggle featureFlag={FeatureFlags.ExperimentalFeatures} label="Experimental Features" />
            <FeatureFlagToggle featureFlag={FeatureFlags.ChatFeature} label="Enable Chat" />
            {chatEnabled && <FeatureFlagToggle featureFlag={FeatureFlags.ChatStreaming} label="Use Streaming Chat" />}
            <FeatureFlagToggle featureFlag={FeatureFlags.SdvCanvasEditor} label="SDV Label Editor" />
        </div>
    );
}

